// Advisor state, kept out of the main store on purpose: the simulation bridge in src/state/store.ts is
// the sim's business, and a language model that is slow, wrong or absent must never be able to stall it.
// This store owns the three current plays, which card is expanded, the player's edits to an expanded
// play, and the one action that crosses the line into the simulation - issuing a play the human clicked.
//
// Spec §6 "propose, vet, then act": `executePlay`/`executeEdited` are the only places an advisor's words
// become orders, and they only ever pass on orders that `validatePlay` already accepted.

import { create } from 'zustand';
import { Advisors, type AdvisorPlayState } from '../ai/advisors';
import { PERSONA_IDS, type AdvisorBrief, type AdvisorContext, type Persona } from '../ai/prompts';
import type { ValidOrder } from '../ai/validate';
import { buildOpord } from '../ui/opord';
import { UNIT_DEFS } from '../sim/scenario';
import { planUnits, useStore } from './store';
import type { AoMeta, Dials, Event, Order, Scenario } from '../sim/types';

export type AdvisorMode = 'planning' | 'live';

/** Where a retargeted card is in its round trip through the map's targeting mode. */
interface Retargeting {
  persona: Persona;
  order: ValidOrder;
  /** Sim tick the card was handed to the map; an order issued before this is somebody else's. */
  armedTick: number;
  /** Wall-clock deadline for giving up on a match after the player left targeting mode. */
  disarmAt: number | null;
}

interface AdvisorsState {
  plays: Record<Persona, AdvisorPlayState | null>;
  refreshing: Record<Persona, boolean>;
  /**
   * The rationale of the answer currently streaming in, as far as it has arrived (pitch 3). The card
   * shows this under a dimmed copy of the previous play, so a refresh is never a spinner: the reader
   * watches the new reasoning being written while the old advice is still standing.
   */
  streaming: Record<Persona, string | null>;
  expanded: Persona | null;
  /** The expanded play's orders as the player has edited them; null means "unedited". */
  edits: Record<Persona, ValidOrder[] | null>;
  retargeting: Retargeting | null;
  /**
   * True once the stage's map has finished loading. Nothing is asked of the model before then: a model
   * call holds a connection to the same origin for up to the whole timeout, and MapLibre needs several
   * of the browser's six to fetch its worker and tiles. Advisor calls used to win that race and the map
   * stayed black until they timed out.
   */
  mapReady: boolean;
  setMapReady: (ready: boolean) => void;

  attach: (mode: AdvisorMode) => () => void;
  expand: (persona: Persona | null) => void;
  removeOrder: (persona: Persona, idx: number) => void;
  retarget: (persona: Persona, idx: number) => void;
  /** Issues the play as proposed. `adopt` replaces the simulation in the planning stage. */
  executePlay: (persona: Persona, adopt?: (orders: ValidOrder[]) => void) => void;
  /** Issues the expanded play as edited (removals and retargets applied). */
  executeEdited: (persona: Persona, adopt?: (orders: ValidOrder[]) => void) => void;
}

const empty = <T,>(v: T): Record<Persona, T> => ({ maneuver: v, fires: v, recon: v });

/** How long to keep watching for the order a retarget was supposed to produce. */
const RETARGET_GRACE_MS = 3000;

/** How long a detached rail is kept alive, so a StrictMode remount reuses it instead of rebuilding. */
const TEARDOWN_DELAY_MS = 50;

/** The one live wiring between the rail and the main store, shared by however many copies are mounted. */
let session: { mode: AdvisorMode; mounts: number; teardown: ReturnType<typeof setTimeout> | null; detach: () => void } | null = null;

function releaseSession(mode: AdvisorMode): void {
  if (!session || session.mode !== mode) return;
  const releasing = session;
  releasing.mounts--;
  if (releasing.mounts > 0 || releasing.teardown !== null) return;
  releasing.teardown = setTimeout(() => {
    // Detach *this* wiring, not whatever `session` points at by the time the timer fires. Leaving the
    // planning stage for the live stage releases the planning session and attaches the live one in the
    // same React commit, and this callback runs fifty milliseconds later: an unguarded `session.detach()`
    // tore down the freshly built live rail instead. The symptom was silent and complete — on the
    // planning -> live path the advisors sat on their seeded templates for the whole fight and never
    // made a single model call, while the quick-start path (no earlier session, so no stale timer)
    // worked perfectly.
    if (session === releasing) releasing.detach();
  }, TEARDOWN_DELAY_MS);
}

/**
 * The run brief: the operation order, the task organization and the plan, as the advisors' cached
 * system block wants them (src/ai/prompts.ts `AdvisorBrief`).
 *
 * Memoized on everything it is built from, for two reasons. It walks the whole terrain-cost layer for
 * the going line, and - much more importantly - the system block is only cacheable upstream if it is
 * byte-identical from one refresh to the next, so rebuilding it per call would be a standing invitation
 * for something non-deterministic to creep in unnoticed. One object per (scenario, dials, plan) means
 * one prompt prefix.
 */
let briefMemo: { key: string; brief: AdvisorBrief } | null = null;

export function runBrief(): AdvisorBrief | null {
  const { scenario, meta, dials, buffers, plan, reserve } = useStore.getState();
  if (!scenario || !meta) return null;

  const key = JSON.stringify([scenario.ao, scenario.seed, dials, plan, reserve]);
  if (briefMemo && briefMemo.key === key) return briefMemo.brief;

  const brief = buildBrief(scenario, meta, dials, buffers?.cost);
  briefMemo = { key, brief };
  return brief;
}

function buildBrief(
  scenario: Scenario, meta: AoMeta, dials: Dials, cost: ArrayBuffer | undefined,
): AdvisorBrief {
  const opord = buildOpord(scenario, meta, dials, cost ? new Uint8Array(cost) : undefined);
  const { plan, reserve } = useStore.getState();
  return {
    aoTitle: opord.aoTitle,
    mission: opord.mission,
    intent: opord.intent,
    situation: opord.situation,
    taskOrg: scenario.blue.map((p, id) => ({ id, callsign: p.callsign, type: UNIT_DEFS[p.type].label })),
    plan: plan.map((o) => ({ unitId: o.unitId, card: o.card, target: o.target })),
    reserve: [...reserve],
  };
}

/**
 * The facts as they stand. Before the worker exists (the planning stage, and the first moment of the
 * live stage) the task organization stands in for the unit list, with the same ids the simulation will
 * give those units — so a play proposed at planning time is issuable verbatim at H-hour.
 */
export function advisorContext(mode: AdvisorMode): AdvisorContext | null {
  const s = useStore.getState();
  const { grid, scenario, dials, snapshot } = s;
  if (!grid || !scenario) return null;
  const brief = runBrief();

  if (mode === 'planning' || !snapshot) {
    return {
      t: 0,
      units: planUnits(scenario),
      contacts: [],
      graphics: scenario.graphics,
      recentEvents: [],
      objectiveId: scenario.objectiveId,
      dials,
      grid,
      orders: [],
      phase: 'Planning',
      brief,
    };
  }

  return {
    t: snapshot.tick,
    // Blue's own force only: an advisor may not see, let alone task, a red unit.
    units: snapshot.units.filter((u) => u.side === 0),
    contacts: snapshot.picture.contacts,
    graphics: scenario.graphics,
    recentEvents: s.events.filter((e) => e.side === 0 && e.t <= snapshot.tick),
    objectiveId: scenario.objectiveId,
    dials,
    grid,
    orders: snapshot.orders,
    phase: snapshot.phase,
    brief,
  };
}

/** The order a retarget produced, if the player has committed one on the map by now. */
function matchingIssued(orders: Order[], r: Retargeting): Order | undefined {
  return orders.find(
    (o) => o.side === 0 && o.unitId === r.order.unitId && o.card === r.order.card && o.issuedAt >= r.armedTick,
  );
}

export const useAdvisors = create<AdvisorsState>((set, get) => ({
  plays: empty(null),
  refreshing: empty(false),
  streaming: empty<string | null>(null),
  expanded: null,
  edits: empty(null),
  retargeting: null,
  mapReady: false,

  setMapReady(ready) {
    if (get().mapReady === ready) return;
    set({ mapReady: ready });
  },

  /**
   * Wires a fresh `Advisors` to the main store for as long as the rail is mounted. Returns the detach
   * function the component's effect calls on unmount; a second mount starts over rather than sharing,
   * which is what makes the component safe to drop into either stage.
   */
  attach(mode) {
    // React StrictMode mounts, unmounts and mounts again. Tearing down on the first unmount and
    // rebuilding would double every call the rail makes, so a detach is deferred by a tick and an
    // attach that arrives before it cancels the teardown and reuses what is already running.
    if (session && session.mode === mode) {
      if (session.teardown !== null) { clearTimeout(session.teardown); session.teardown = null; }
      session.mounts++;
      return () => releaseSession(mode);
    }
    session?.detach();

    set({
      plays: empty(null), refreshing: empty(false), streaming: empty<string | null>(null),
      expanded: null, edits: empty(null), retargeting: null,
    });

    const advisors = new Advisors({
      getContext: () => advisorContext(mode),
      onState: (persona, state) => {
        set({
          plays: { ...get().plays, [persona]: state },
          // The validated play has landed, so the partial text it was written over goes away.
          streaming: { ...get().streaming, [persona]: null },
        });
        // A new play invalidates edits made to the old one.
        if (get().edits[persona]) set({ edits: { ...get().edits, [persona]: null } });
      },
      onRefreshing: (persona, refreshing) => set({
        refreshing: { ...get().refreshing, [persona]: refreshing },
        ...(refreshing ? {} : { streaming: { ...get().streaming, [persona]: null } }),
      }),
      onPartial: (persona, rationale) => set({ streaming: { ...get().streaming, [persona]: rationale } }),
    });

    // The templates go up at once, model or no model, so a play is always one click away.
    advisors.seedTemplates();

    // The opening round of model calls waits for the map (see `mapReady` above).
    let started = false;
    const startWhenReady = (): void => {
      if (started || !get().mapReady) return;
      started = true;
      void advisors.refresh('planning');
    };
    const unwatchMap = useAdvisors.subscribe((s, prev) => {
      if (s.mapReady && !prev.mapReady) startWhenReady();
    });
    startWhenReady();

    let lastEventId = -1;
    const unsubscribe = useStore.subscribe((s, prev) => {
      // ---- retarget round trip ---------------------------------------------------------------------
      const r = get().retargeting;
      if (r) {
        const issued = s.snapshot ? matchingIssued(s.snapshot.orders, r) : undefined;
        if (issued) {
          // The card went out on its own with the new target, so it leaves the edited play.
          const list = get().edits[r.persona];
          set({
            edits: { ...get().edits, [r.persona]: list ? list.filter((o) => o !== r.order) : null },
            retargeting: null,
          });
        } else if (prev.targeting && !s.targeting) {
          set({ retargeting: { ...r, disarmAt: Date.now() + RETARGET_GRACE_MS } });
        } else if (r.disarmAt !== null && Date.now() > r.disarmAt) {
          set({ retargeting: null }); // the player left targeting without committing; the card stays
        }
      }

      if (mode !== 'live' || !started) return;

      // ---- refresh triggers ------------------------------------------------------------------------
      if (s.events !== prev.events) {
        // Ids, not array length: the log is truncated once it grows past its cap.
        for (const ev of s.events) {
          if (ev.id <= lastEventId) continue;
          lastEventId = ev.id;
          if (ev.significant) void advisors.refresh(ev as Event);
        }
      }
      if (s.snapshot !== prev.snapshot) {
        advisors.seedTemplates(); // the first live snapshot is the first context with real units
        void advisors.refresh('timer');
      }
    });

    session = {
      mode,
      mounts: 1,
      teardown: null,
      detach: () => {
        unwatchMap();
        unsubscribe();
        advisors.dispose();
        session = null;
      },
    };
    return () => releaseSession(mode);
  },

  expand(persona) {
    const expanded = get().expanded === persona ? null : persona;
    set({ expanded, retargeting: null });
    if (expanded) {
      const play = get().plays[expanded];
      // Editing starts from a copy of what was validated, so removals never touch the proposal itself.
      set({ edits: { ...get().edits, [expanded]: [...(play?.validated.orders ?? [])] } });
    }
  },

  removeOrder(persona, idx) {
    const list = get().edits[persona] ?? get().plays[persona]?.validated.orders ?? [];
    set({ edits: { ...get().edits, [persona]: list.filter((_, i) => i !== idx) } });
  },

  /**
   * Hands one card of an expanded play back to the map, in exactly the mode the order deck uses: the
   * unit is selected and the card is armed, so the next click on the map issues it at the new target.
   * The entry then leaves the edited play — it is already on the net — and the rest goes with
   * "execute (edited)".
   */
  retarget(persona, idx) {
    const list = get().edits[persona] ?? get().plays[persona]?.validated.orders ?? [];
    const order = list[idx];
    if (!order) return;
    const s = useStore.getState();
    if (!s.sim) return; // planning has no map targeting yet; the chip is disabled there
    s.select(order.unitId);
    s.startTargeting(order.card);
    set({
      edits: { ...get().edits, [persona]: list },
      retargeting: { persona, order, armedTick: s.snapshot?.tick ?? 0, disarmAt: null },
    });
  },

  executePlay(persona, adopt) {
    const state = get().plays[persona];
    if (!state) return;
    commit(persona, state.play.name, state.validated.orders, adopt);
  },

  executeEdited(persona, adopt) {
    const state = get().plays[persona];
    if (!state) return;
    const orders = get().edits[persona] ?? state.validated.orders;
    commit(persona, state.play.name, orders, adopt);
    set({ expanded: null, edits: { ...get().edits, [persona]: null }, retargeting: null });
  },
}));

/**
 * The one crossing from proposal to action. In the live stage the whole bundle goes to the simulation
 * under one play id, so the debrief can trace every order back to the advisor that proposed it; in the
 * planning stage it lands in the plan instead, either through the caller's `adopt` callback or, failing
 * that, the store's own plan.
 */
function commit(persona: Persona, playName: string, orders: ValidOrder[], adopt?: (orders: ValidOrder[]) => void): void {
  if (orders.length === 0) return;
  if (adopt) { adopt(orders); return; }

  const s = useStore.getState();
  if (s.sim) {
    // Executing a play is the player giving orders: it holds an auto-slow that was counting itself out
    // (src/state/store.ts `noteInteraction`).
    s.noteInteraction();
    s.sim.issuePlay(`${persona}:${playName}`, orders);
    return;
  }
  for (const o of orders) s.addPlanOrder(o);
}

/** Convenience for the cards: the three personas in rail order. */
export { PERSONA_IDS };
